import Vue from 'vue';
import CallBuilder from '@/services/call-builder.service';
import PlaySounds from '@/services/play-sounds.service';
import api from '@/api';
import router from '@/router';
import { modals } from '@/constants/modals.constant';
import { REMOTE_PATHS } from '@/constants/remote-paths.constant';
import { Socket } from 'phoenix';

const state = {
  slot: null,
  status: null,
  credentials: null,
  peerConnection: null,
  owner: null,
  user: null,
  localStream: null,
  remoteStream: null,
  initializing: true,
  devices: {},
  currentInput: localStorage.getItem('default-audio-input') || 'default',
  currentOutput: localStorage.getItem('default-audio-output') || 'default',
  conferenceBuilder: new CallBuilder(),
  startedAt: null,
  aside: null,
  recentConferences: null,
  ownerIsPresent: null,
  participantsCount: 0,
  callChannelSocket: null,
  messagesSent: [],
  sequence: 1,
  playSounds: new PlaySounds(),
  currentRoomParticipantsCount: null,
  broadcast: null
};

const getters = {
  participantsCount(state) {
    return state.participantsCount;
  },
  ownerIsPresent(state) {
    return state.ownerIsPresent;
  },
  aside(state) {
    return state.aside;
  },
  conference(state) {
    return {
      status: state.status,
      credentials: state.credentials
    };
  },
  user(state) {
    return state.user || {};
  },
  owner(state) {
    return state.owner || {};
  },
  initializing(state) {
    return state.initializing;
  },
  devices(state) {
    const devices = {
      input: [],
      output: []
    };
    if (state.devices.length) {
      state.devices.forEach(({label, deviceId, kind}) => {
        if (kind === 'audioinput') {
          const device = {label, deviceId};
          devices.input.push(device);
          if (deviceId === state.currentInput) {
            devices.selectedInput = device;
          }
        }
        if (kind === 'audiooutput') {
          const device = {label, deviceId};
          devices.output.push(device);
          if (deviceId === state.currentOutput) {
            devices.selectedOutput = device;
          }
        }
      });
    }
    return devices;
  },
  localStream(state) {
    return state.localStream;
  },
  localStreamSrc(state) {
    return state.localStream;
  },
  remoteStreamSrc(state) {
    return state.remoteStream;
  },
  startedAt(state) {
    return state.startedAt;
  },
  broadcast(state) {
    return state.broadcast || {};
  }
};

const actions = {
  joinRoom(
    { state, commit, dispatch },
    { pin, username }
  ) {
    state.playSounds.play('call_connecting');
    state.credentials = {
      conferenceUrl: window.location.href,
      conferencePin: pin,
      username
    };

    const buffer = state.conferenceBuilder.createCreateSlotRequestBody({pin, username});
    api.createSlot(buffer).then(response => {
      state.slot = state.conferenceBuilder.decodeCreateSlotResponse(response.body);
      state.slot.iceServers.forEach(iceServer => {
        iceServer.urls = iceServer.uri;
        iceServer.credential = iceServer.password;
      });
      dispatch('connectWebsocket', state.slot.slotId);
    },
    err => {
      dispatch('handleCreateSlotError', err);
    });
  },

  setPeerConnection({ state, commit, dispatch, rootState }) {
    state.startedAt = Math.trunc((new Date()).getTime() / 1000);
    dispatch('startStream');
    dispatch('joinChat', state.slot.chatChannelName);
  },

  startStream(
    { state, commit, dispatch },
    deviceId
  ) {
    return new Promise(resolve => {
      const getUserMedia = navigator.getUserMedia ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia;

      if (deviceId) {
        state.currentInput = deviceId;
      }

      let audio = true;
      if (state.currentInput) {
        audio = {
          optional: [{
            sourceId: state.currentInput
          }]
        };
      }

      getUserMedia.call(navigator,
        {audio: true, video: false},
        stream => {
          dispatch('gotStream', stream);
          resolve(stream);
        },
        error => {}
      );
    });
  },

  gotStream({ state, commit, dispatch }, stream) {
    state.localStream = stream;
    const PeerConnection = window.webkitPeerConnection00 ||
      window.webkitRTCPeerConnection ||
      window.mozRTCPeerConnection ||
      window.RTCPeerConnection ||
      window.PeerConnection;

    state.peerConnection = new PeerConnection(
      {iceServers: state.slot.iceServers},
      {optional: [{DtlsSrtpKeyAgreement: true}]}
    );

    state.peerConnection.onicecandidate = event => dispatch('gotIceCandidate', event);
    state.peerConnection.onaddstream = event => dispatch('gotRemoteStream', event);
    state.peerConnection.addStream(state.localStream);

    state.peerConnection.createOffer(
      desc => dispatch('gotDescription', desc),
      err => console.warn(err),
      {mandatory: {OfferToReceiveAudio: true}}
    );
  },

  gotDescription({ state, commit, dispatch }, desc) {
    state.peerConnection.setLocalDescription(desc);
    const body = state.conferenceBuilder.createSessionDescriptionBody(
      desc,
      state.sequence
    );
    dispatch('sendSocketMessage', body);
    commit('keepSequence', body);
  },

  gotIceCandidate({ state, commit, dispatch }, event) {
    if (event.candidate) {
      const body = state.conferenceBuilder.createIceCandidateBody(
        event.candidate,
        state.sequence
      );
      dispatch('sendSocketMessage', body);
      commit('keepSequence', body);
    }
  },

  setRemoteDescription({ state }, { type, description}) {
    const types = ['unknown', 'offer', 'pranswer', 'answer'];
    state.peerConnection.setRemoteDescription(new RTCSessionDescription({
      type: types[type],
      sdp: description
    }));
  },

  gotRemoteStream({ state, dispatch }, event) {
    // document.getElementById('remoteStream').srcObject = event.stream;
    state.remoteStream = event.stream;
    if (state.currentOutput) {
      dispatch('attachSinkId', state.currentOutput);
    }
  },

  muteCall({ state }) {
    const body = state.conferenceBuilder.createMuteRequestBody(!state.user.isMuted);
    api.muteCall(body, state.slot.slotId);
  },

  lockCall({ state }) {
    const id = 'confirm_lock_conference';
    const body = state.conferenceBuilder.createLockRequestBody(!state.status.locked);
    if (
      !localStorage.getItem(id) &&
      !state.status.locked
    ) {
      Vue.prototype.$modal.show('confirmation-modal', {
        id,
        title: modals[id].title,
        description: modals[id].description,
        rememberOption: true,
        actions: [
          {
            label: 'cancel',
            class: 'cancel-btn'
          },
          {
            label: 'lock',
            class: 'submit-btn',
            handler: () => {
              api.lockCall(body, state.slot.slotId);
            }
          }
        ]
      });
    } else {
      api.lockCall(body, state.slot.slotId);
    }
  },

  muteAll({ state }) {
    const id = 'confirm_mute_all';
    if (
      !localStorage.getItem(id) &&
      !state.status.locked
    ) {
      Vue.prototype.$modal.show('confirmation-modal', {
        id,
        title: modals[id].title,
        description: modals[id].description,
        rememberOption: true,
        actions: [
          {
            label: 'cancel',
            class: 'cancel-btn'
          },
          {
            label: 'mute all',
            class: 'submit-btn',
            handler: () => {
              state.playSounds.play('call_mute');
              api.muteAll(state.slot.slotId).then(() => {
                state.playSounds.play('call_muted');
              });
            }
          }
        ]
      });
    } else {
      state.playSounds.play('call_mute');
      api.muteAll(state.slot.slotId).then(() => {
        state.playSounds.play('call_muted');
      });
    }
  },

  endCall({ state }) {
    if (state.user.isOwner) {
      api.endCall();
    } else {
      const body = state.conferenceBuilder.createEndSlotRequest();
      api.leaveCall(
        state.slot.slotId,
        body
      );
    }
  },

  mutedByAdmin({ state, commit }, { whoUserId }) {
    if (state.user.userId !== whoUserId) {
      const id = 'confirm_muted_by_admin';
      if (!localStorage.getItem(id)) {
        Vue.prototype.$modal.show('confirmation-modal', {
          id,
          title: modals[id].title,
          description: modals[id].description,
          rememberOption: true,
          actions: [
            {
              label: 'ok, got it',
              class: 'submit-btn'
            }
          ]
        });
      }
    }
  },

  closeConference({ state, commit }, code) {
    // code 1 - ended by admin; 2 - kicked by admin
    if (
      (code === 1 && !state.user.isOwner) ||
      code === 2
    ) {
      const id = `prompt_conference_${code === 1 ? 'ended' : 'kicked'}`;
      Vue.prototype.$modal.show('confirmation-modal', {
        id,
        title: modals[id].title,
        description: modals[id].description,
        rememberOption: code === 2,
        actions: [
          {
            label: 'ok, got it',
            class: 'submit-btn'
          }
        ]
      });
    }
    if (state.peerConnection) {
      state.peerConnection.close();
    }
    if (
      state.localStream &&
      state.localStream.getTracks()[0]
    ) {
      state.localStream.getTracks()[0].stop();
    }
    state.playSounds.play('call_ended');
    commit('clearConferenceState');
    router.push('/');
  },

  attachSinkId({ state }, sinkId) {
    const element = document.getElementById('remoteStream');
    if (
      sinkId &&
      element &&
      typeof element.sinkId !== 'undefined'
      ) {
      element.setSinkId(sinkId).then(() => {
        if (state.currentOutput !== sinkId) {
          state.currentOutput = sinkId;
        }
      });
    }
  },

  setDefaultDevices({ state, dispatch }) {
    state.currentInput = localStorage.getItem('default-audio-input') || 'default';
    state.currentOutput = localStorage.getItem('default-audio-output') || 'default';
    dispatch('attachSinkId', state.currentOutput);
    dispatch('startStream');
  },

  setConferenceTitle({ state }, title) {
    const body = state.conferenceBuilder.createTitleRequestBody(title);
    api.setTitle(body, state.slot.slotId);
  },

  muteUser({ state }, userId) {
    const body = state.conferenceBuilder.createMuteUserRequestBody(userId);
    api.muteUser(body, state.slot.slotId);
  },

  unmuteUser({ state }, userId) {
    const body = state.conferenceBuilder.createUnmuteUserRequestBody(userId);
    api.unmuteUser(body, state.slot.slotId);
  },

  kickUser({ state }, userId) {
    const body = state.conferenceBuilder.createKickUserRequestBody(userId);
    api.kickUser(body, state.slot.slotId);
  },

  putAdminRights({ state }, {userId, isAdmin}) {
    const body = state.conferenceBuilder.createPutAdminRequestBody(isAdmin);
    api.putAdminRights(body, state.slot.slotId, userId);
  },

  toggleAside({ state, commit }, type) {
    if (state.aside === type) {
      state.aside = false;
    } else {
      const timeout = state.aside ? 300 : 0;
      state.aside = false;
      setTimeout(() => {
        state.aside = type;
        if (type === 'chat') {
          commit('resetUnreadMessagesCount');
        }
      }, timeout);
    }
  },

  updateConference(
    { state, commit, dispatch, rootState },
    { updateStatus, updateCredentials }
  ) {
    state.user = rootState.user.currentUser;
    if (updateStatus) {
      let ownerIsPresent = false;
      updateStatus.participants.forEach(user => {
        if (user.userId === updateStatus.ownerUserId) {
          ownerIsPresent = true;
        }
        if (state.user && user.userId === state.user.userId) {
          state.user.isAdmin = user.isAdmin;
          state.user.isMuted = user.isMuted;
        }
        dispatch('getUserInfo', user.userId);
      });
      commit('updateStatus', {
        status: updateStatus,
        ownerIsPresent
      });
      if (!state.owner && state.status.ownerUserId !== 'unavailable') {
        dispatch('getUserInfo', state.status.ownerUserId).then(owner => {
          state.owner = owner;
          if (state.owner.userId !== rootState.user.currentUser.userId) {
            commit('updateRecentConferences');
          }
        });
      }
      state.user.isOwner = state.status.ownerUserId === state.status.connectedUserId;
      if (state.initializing) {
        state.initializing = false;
        state.playSounds.play('call_successfully_connected_sound').then(() => {
          if (updateStatus.participants.length === 1) {
            state.playSounds.play('call_successfully_connected_voice_paid').then(() => {
              state.playSounds.play('call_welcome').then(() => {
                if (state.user.isOwner) {
                  state.playSounds.play('call_welcome_no_participants_admin').then(() => {
                    state.playSounds.play('waiting_for_participants', true);
                  });
                } else {
                  state.playSounds.play('call_welcome_no_participants').then(() => {
                    state.playSounds.play('waiting_for_participants', true);
                  });
                }
              });
            });
          }
        });
      }
    }
    if (updateCredentials) {
      commit('updateCredentials', updateCredentials);
    }
  },

  connectCallChannelSocket({ state, commit, dispatch, rootState }) {
    const socketParams = {
      decode: (payload, callback) => {
        state.conferenceBuilder.decodePhoenix(payload, callback);
      },
      params: {
        authorization: `Bearer ${Vue.prototype.$cookie.get('accessToken')}`
      }
    };

    state.callChannelSocket = new Socket(
      `${REMOTE_PATHS.WS_PATH}/conference/call_channel`,
      socketParams
    );
    state.callChannelSocket.onConnMessage = response => {
      state.conferenceBuilder.decodeCallChannel(response.data, message => {
        state.participantsCount = message.count;
      });
    };
    if (process.env.NODE_ENV !== 'development') {
      state.callChannelSocket.connect();
    }
    if (rootState.user.currentUser) {
      const channel = state.callChannelSocket.channel(
        `participants_count:${rootState.user.currentUser.userId}`,
        {}
      );
      channel.join()
        .receive('ok', ({messages}) => console.log('catching up', messages))
        .receive('error', ({reason}) => console.log('failed join', reason))
        .receive('timeout', () => console.log('Networking issue. Still waiting...'));
      channel.on('participants_count', message => {
        console.info('on participants_count', message);
      });
      channel.on('participantsCount', message => {
        console.info('on participantsCount', message);
      });
    }
  },

  disconnectCallChannelSocket({ state }) {
    state.callChannelSocket.disconnect();
    state.callChannelSocket = null;
    state.participantsCount = 0;
  },

  handleCreateSlotError({ state }, err) {
    const message = state.conferenceBuilder.decodeCreateSlotError(err.data);
    router.push('/');
    let reason;
    switch (message.code) {
      case 1:
        reason = 'incorrect_credentials';
        break;
      case 2:
        reason = 'room_locked';
        state.playSounds.play('call_locked');
        break;
      case 3:
        reason = 'room_full';
        state.playSounds.play('call_full');
        break;
      default:
        reason = 'unknown';
        break;
    }
    const id = `prompt_join_error_${reason}`;
    Vue.prototype.$modal.show('confirmation-modal', {
      id,
      title: modals[id].title,
      description: modals[id].description,
      rememberOption: false,
      actions: [
        {
          label: 'ok, got it',
          class: 'submit-btn'
        }
      ]
    });
  },

  broadcast({ state, commit, dispatch }, rstp) {
    return new Promise(resolve => {
      commit('setBroadcast', {
        url: rstp,
        pin: 123,
        startedAt: Math.trunc((new Date()).getTime() / 1000)
      });
      resolve(state.broadcast);
    });
  }
};

const mutations = {
  updateRecentConferences(state) {
    state.recentConferences = JSON.parse(
      localStorage.getItem(`recent-conferences-${state.user.userId}`)
    ) || [];
    let index;
    state.recentConferences.forEach((v, i) => {
      if (v.ownerId === state.owner.userId) {
        index = i;
      }
    });

    if (index >= 0) {
      if (state.recentConferences <= 0) {
        let k = 0 - state.recentConferences;
        while ((k--) + 1) {
          state.recentConferences.push(undefined);
        }
      }
      state.recentConferences.splice(0, 0, state.recentConferences.splice(index, 1)[0]);
    } else {
      state.recentConferences.unshift({
        ownerId: state.owner.userId,
        avatarUrl: state.owner.avatarUrl,
        conferencePin: state.credentials.conferencePin,
        username: state.owner.username
      });
    }

    if (state.recentConferences.length > 8) {
      state.recentConferences.splice(-1, 1);
    }
    localStorage.setItem(
      `recent-conferences-${state.user.userId}`,
      JSON.stringify(state.recentConferences)
    );
  },

  clearConferenceState(state) {
    state.slot = null;
    state.status = null;
    state.credentials = null;
    state.peerConnection = null;
    state.owner = null;
    state.user = null;
    state.initializing = true;
    state.startedAt = null;
    state.aside = null;
    state.ownerIsPresent = null;
    state.sequence = 1;
    state.messagesSent = [];
  },

  updateStatus(state, { status, ownerIsPresent}) {
    state.status = status;
    state.ownerIsPresent = ownerIsPresent;
    if (state.status.participants.length !== state.currentRoomParticipantsCount) {
      if (
        state.status.participants.length > 1 &&
        state.status.participants.length > state.currentRoomParticipantsCount
      ) {
        state.playSounds.play('call_participant_joined');
      }

      if (
        state.status.participants.length >= 1 &&
        state.status.participants.length < state.currentRoomParticipantsCount
      ) {
        state.playSounds.play('call_participant_left');
      }
      state.currentRoomParticipantsCount = state.status.participants.length;
    }
  },

  updateCredentials(state, credentials) {
    state.credentials = credentials;
  },

  getDevices(state) {
    navigator.mediaDevices.enumerateDevices().then(devices => {
      state.devices = devices.filter(d => d.kind === 'audioinput' || d.kind === 'audiooutput');
    }).catch(e => {
      console.warn('Can\'t get devices list', e);
    });
  },

  keepSequence(state, messageBody) {
    state.sequence++;
    state.messagesSent.push(messageBody);
  },

  setBroadcast(state, value) {
    state.broadcast = value;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/conference/conference.js